// src/ui/authUI.js
// Refactored for Unified Auth Overlay & Enhanced Leaderboard

// --- Service & Config Imports ---
import UIController from "./uiController.js";
import {
  signUpWithEmail,
  signInWithEmail,
  loadTopScores // Used by leaderboard logic
} from "../services/authService.js";
// Removed updateUserConsent import - assumed handled in ProfileScene or similar
import { auth, db, firebase } from "../firebaseConfig.js";
import { clearTokens } from "../services/tokenService.js";
// Keep uiUtils import ONLY if hideGameOverOverlay is still needed externally



// --- Loading Indicator Utilities ---
function showLoadingIndicator(elementId) {
  const element = document.getElementById(elementId);
  if (!element) return;
  element.dataset.originalText = element.textContent || "Submit";
  element.textContent = "Loading...";
  element.disabled = true;
  element.style.opacity = "0.7";
  element.style.cursor = "wait";
}

function hideLoadingIndicator(elementId) {
  const element = document.getElementById(elementId);
  if (!element) return;
  element.textContent = element.dataset.originalText || "Submit";
  element.disabled = false;
  element.style.opacity = "1";
  element.style.cursor = "pointer";
}
// --- End Loading ---


// --- Unified Auth Overlay Elements & Logic ---
let authOverlay, authOverlayContent, authTitle, authEmailInput, authPasswordInput;
let signupSection, signupUsernameInput, signupPassword2Input;
let privacyConsentAuth, marketingConsentAuth, dataSharingConsentAuth;
let signinSubmitBtn, signupSubmitBtn;
let switchToSignupLinkContainer, switchToSigninLinkContainer;
let authMessageDiv;



function cacheAuthElements() {
  authOverlay = document.getElementById("auth-overlay");
  authOverlayContent = document.getElementById("auth-overlay-content");
  authTitle = document.getElementById("auth-title");
  authEmailInput = document.getElementById("auth-email");
  authPasswordInput = document.getElementById("auth-password");
  signupSection = document.getElementById("signup-section");
  signupUsernameInput = document.getElementById("signup-username");
  signupPassword2Input = document.getElementById("signup-password2");
  privacyConsentAuth = document.getElementById("privacy-consent-auth");
  marketingConsentAuth = document.getElementById("marketing-consent-auth");
  dataSharingConsentAuth = document.getElementById("data-sharing-consent-auth");
  signinSubmitBtn = document.getElementById("signin-submit-btn-auth");
  signupSubmitBtn = document.getElementById("signup-submit-btn-auth");
  switchToSignupLinkContainer = document.getElementById("switch-to-signup");
  switchToSigninLinkContainer = document.getElementById("switch-to-signin");
  authMessageDiv = document.getElementById("auth-message") ||
                   document.getElementById("auth-overlay-message");
}

function setAuthMessage(message, isError = true) {
  if (!authMessageDiv) cacheAuthElements();
  if (authMessageDiv) {
    authMessageDiv.textContent = message;
    authMessageDiv.style.color = isError ? '#ef4444' : '#10b981'; // Professional colors
    authMessageDiv.style.display = message ? 'block' : 'none';
  }
}

// --- Auth Overlay Functions ---
export async function showAuthOverlay(mode = 'signin') {
  console.log(`showAuthOverlay called with mode: ${mode} - using original auth system`);
  
  // CRITICAL: Don't call EnterpriseAuthService from here to avoid circular calls
  // EnterpriseAuthService calls this function, so this should be the base implementation
  
  // Try professional dialogs first - RESTORED
  if (professionalAuthDialogs) {
    console.log('🎨 Using professional authentication dialogs (RESTORED)');
    if (mode === 'signin') {
      professionalAuthDialogs.showSignIn();
    } else {
      professionalAuthDialogs.showSignUp();
    }
    return;
  }
  
  // FALLBACK: Use original basic auth overlay system
  console.log('📝 Using basic auth overlay system');
  
  // Cache elements if needed
  if (!authOverlay) cacheAuthElements();
  
  if (!authOverlay || !authTitle || !signinSubmitBtn || !signupSubmitBtn ||
      !switchToSignupLinkContainer || !switchToSigninLinkContainer ||
      !signupSection || !authEmailInput || !authPasswordInput ||
      !signupUsernameInput || !signupPassword2Input ||
      !privacyConsentAuth || !marketingConsentAuth || !dataSharingConsentAuth) {
    console.error("Auth overlay crucial elements not found in DOM! Cannot show overlay.");
    alert("UI Error: Authentication form is missing.");
    return;
  }

  // Clear input fields and error messages
  authEmailInput.value = '';
  authPasswordInput.value = '';
  signupUsernameInput.value = '';
  signupPassword2Input.value = '';
  privacyConsentAuth.checked = false;
  marketingConsentAuth.checked = false;
  dataSharingConsentAuth.checked = false;
  setAuthMessage("");

  // Configure the overlay based on mode
  if (mode === 'signin') {
    authTitle.textContent = "Welcome back";
    signupSection.classList.add('hidden-section');
    signinSubmitBtn.classList.remove('hidden-section');
    signupSubmitBtn.classList.add('hidden-section');
    switchToSignupLinkContainer.classList.remove('hidden-section');
    switchToSigninLinkContainer.classList.add('hidden-section');
    authPasswordInput.autocomplete = "current-password";
    hideLoadingIndicator('signin-submit-btn-auth');
  } else {
    authTitle.textContent = "Create Account";
    signupSection.classList.remove('hidden-section');
    signinSubmitBtn.classList.add('hidden-section');
    signupSubmitBtn.classList.remove('hidden-section');
    switchToSignupLinkContainer.classList.add('hidden-section');
    switchToSigninLinkContainer.classList.remove('hidden-section');
    authPasswordInput.autocomplete = "new-password";
    hideLoadingIndicator('signup-submit-btn-auth');
  }

  // ENHANCED: Apply desktop design classes for the new styling
  console.log('🎨 Applying enhanced desktop authentication styling');
  authOverlay.className = 'overlay desktop-auth-overlay';
  
  // DEBUG: Confirm overlay element and styling
  console.log('🔍 DEBUG: Auth overlay element:', authOverlay);
  console.log('🔍 DEBUG: Auth overlay classes:', authOverlay.className);
  console.log('🔍 DEBUG: Auth overlay content element:', document.getElementById('auth-overlay-content'));
  
  // Use UIController if available; otherwise fallback to direct DOM manipulation
  if (UIController && typeof UIController.showOverlay === 'function') {
    console.log("Using UIController to show enhanced auth overlay");
    UIController.showOverlay('auth-overlay');
  } else {
    console.warn("UIController not available, using direct DOM manipulation");
    authOverlay.style.display = 'flex';
    void authOverlay.offsetHeight; // Force reflow for transition
    authOverlay.classList.add("show");
    try {
      if (window.gameInstance && window.gameInstance.getActiveScene) {
        const activeScene = window.gameInstance.getActiveScene();
        if (activeScene && activeScene.input) {
          activeScene.input.enabled = false;
        }
      }
    } catch (e) {
      console.warn("Could not disable game input during overlay show:", e);
    }
  }
  
  setTimeout(() => {
    try { authEmailInput.focus(); } catch (e) {}
  }, 150);
}

// In authUI.js - Modify hideAuthOverlay function
export function hideAuthOverlay() {
  // Prevent immediate showing of other overlays by adding a small delay flag
  window.authOverlayJustClosed = true;
  
  if (UIController && typeof UIController.hideOverlay === 'function') {
    UIController.hideOverlay('auth-overlay');
  } else if (authOverlay) {
    authOverlay.classList.remove("show");
    authOverlay.style.display = 'none';
  }
  
  // Clear the flag after a short delay
  setTimeout(() => {
    window.authOverlayJustClosed = false;
  }, 300);
}

// --- Animation Helpers (for overlays) ---
function showOverlayWithAnimation(overlay) {
  if (!overlay) return;
  overlay.style.opacity = '0';
  overlay.classList.add("show");
  void overlay.offsetWidth;
  overlay.style.transition = 'opacity 0.3s ease';
  overlay.style.opacity = '1';
}

function hideOverlayWithAnimation(overlay) {
  if (!overlay) return;
  overlay.style.transition = 'opacity 0.3s ease';
  overlay.style.opacity = '0';
  setTimeout(() => {
    overlay.classList.remove("show");
  }, 300);
}
// --- End Animation Helpers ---

function addLeaderboardStyles() {
  // Check if styles already exist
  if (document.getElementById('leaderboard-enhanced-styles')) {
    return;
  }
  
  // Create style element
  const styleEl = document.createElement('style');
  styleEl.id = 'leaderboard-enhanced-styles';
  styleEl.innerHTML = `
    /* Main leaderboard container */
    #leaderboard-overlay .overlay-content {
      max-width: 90%;
      width: 800px;
      margin: 0 auto;
      padding: 20px;
    }
    
    /* Tabs container styles */
    #leaderboard-tabs-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      gap: 15px;
      margin-bottom: 20px;
    }
    
    /* Filter groups for buttons */
    .filter-group {
      display: flex;
      justify-content: center;
      gap: 10px;
      width: 100%;
    }
    
    /* Individual filter buttons */
    .filter-button {
      padding: 8px 16px;
      border: none;
      border-radius: 4px;
      background-color: #444444;
      color: #FFFFFF;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    
    .filter-button.active {
      transform: scale(1.05);
      font-weight: bold;
    }
    
    .filter-button:hover:not(.active) {
      transform: scale(1.03);
    }
    
    /* Level 1 buttons */
    .level-one .filter-button {
      min-width: 120px;
    }
    
    .level-one .filter-button.active {
      background-color: #00AAFF;
    }
    
    .level-one .filter-button:hover:not(.active) {
      background-color: #0088CC;
    }
    
    /* Level 2 buttons */
    .level-two .filter-button {
      min-width: 80px;
    }
    
    .level-two .filter-button.active {
      background-color: #00CC88;
    }
    
    .level-two .filter-button:hover:not(.active) {
      background-color: #009966;
    }
    
    /* Table styles */
    .leaderboard-table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 10px;
      background-color: rgba(0, 0, 0, 0.3);
    }
    
    .leaderboard-table th {
      padding: 10px;
      background-color: rgba(0, 0, 0, 0.5);
      color: #FFFFFF;
      font-weight: bold;
      text-align: center;
    }
    
    .leaderboard-table td {
      padding: 8px;
      text-align: center;
    }
    
    .leaderboard-table tr:nth-child(odd) {
      background-color: rgba(0, 0, 0, 0.1);
    }
    
    .leaderboard-table tr:nth-child(even) {
      background-color: rgba(0, 0, 0, 0.2);
    }
    
    .leaderboard-table tr:hover {
      background-color: rgba(0, 100, 255, 0.2);
    }
    
    /* Verified player marker */
    .verified-star {
      color: #10b981; /* Professional success green */
      margin-left: 4px;
      font-size: 1.2em;
    }
    
    /* Title and headers */
    .leaderboard-view-title {
      margin-bottom: 15px;
      text-align: center;
      color: var(--color-primary, #00d4ff); /* Professional blue */
      font-size: 22px;
    }
    
    /* Empty state and notes */
    .no-scores {
      text-align: center;
      padding: 20px;
      color: #CCCCCC;
    }
    
    .leaderboard-note {
      margin-top: 15px;
      text-align: center;
      font-size: 12px;
      color: #CCCCCC;
    }
    
    /* Loading indicator */
    .loading-indicator {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100px;
      width: 100%;
      color: #CCCCCC;
    }
    
    /* Error message */
    .error-message {
      text-align: center;
      color: #FF5555;
      padding: 20px;
    }
  `;
  
  // Add style to document
  document.head.appendChild(styleEl);
}
function formatPlayerName(name, maxLength = 20) {
  if (!name) return 'Player';
  
  // If name is already short enough, return it as is
  if (name.length <= maxLength) return name;
  
  // Otherwise truncate with ellipsis
  return name.substring(0, maxLength - 3) + '...';
}

function formatScore(score) {
  if (typeof score !== 'number') return '0';
  return score.toLocaleString();
}

function formatDate(date) {
  if (!date) return '-';
  
  try {
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short'
    });
  } catch (error) {
    console.warn('Error formatting date:', error);
    return '-';
  }
}
// --- Leaderboard Overlay Logic ---
let leaderboardOverlay, leaderboardContent, leaderboardList, leaderboardTabs;
let currentLeaderboardFilter = { game: 'All', mode: 'All' };

// Export for HallOfFameDOM access
export { currentLeaderboardFilter };
let leaderboardElementsCached = false;

function cacheLeaderboardElements() {
  if (leaderboardElementsCached) return true;

  console.log("Attempting to cache leaderboard elements...");
  leaderboardOverlay = document.getElementById("leaderboard-overlay");
  leaderboardContent = document.getElementById("leaderboard-overlay-content");
  const contentArea = document.getElementById("leaderboard-content"); // Fixed: matches HTML id
  leaderboardList = document.getElementById("leaderboard-list");
  leaderboardTabs = document.getElementById("leaderboard-tabs"); // Fixed: matches HTML id

  if (leaderboardOverlay && leaderboardContent && contentArea && leaderboardList && leaderboardTabs) {
    console.log("Leaderboard elements successfully cached.");
    
    // Add enhanced styles once elements are cached
    addLeaderboardStyles();
    
    // Set default styles for elements that will persist across all tab/game changes
    setupBaseLeaderboardStyles();
    
    leaderboardElementsCached = true;
    return true;
  } else {
    console.error("Failed to cache leaderboard elements. Missing one or more critical IDs:", {
      overlay: !!leaderboardOverlay,
      content: !!leaderboardContent,
      contentArea: !!contentArea,
      list: !!leaderboardList,
      tabs: !!leaderboardTabs
    });
    leaderboardElementsCached = false;
    return false;
  }
}
function setupBaseLeaderboardStyles() {
  // Main content area style
  if (leaderboardContent) {
    leaderboardContent.style.display = 'flex';
    leaderboardContent.style.flexDirection = 'column';
    leaderboardContent.style.alignItems = 'center';
    leaderboardContent.style.padding = '20px';
    leaderboardContent.style.maxWidth = '90%';
    leaderboardContent.style.margin = '0 auto';
  }
  
  // Tabs container style
  if (leaderboardTabs) {
    leaderboardTabs.style.display = 'flex';
    leaderboardTabs.style.flexDirection = 'column';
    leaderboardTabs.style.alignItems = 'center';
    leaderboardTabs.style.width = '100%';
    leaderboardTabs.style.gap = '15px';
    leaderboardTabs.style.marginBottom = '20px';
  }
  
  // List style
  if (leaderboardList) {
    leaderboardList.style.width = '100%';
    leaderboardList.style.display = 'flex';
    leaderboardList.style.flexDirection = 'column';
    leaderboardList.style.alignItems = 'center';
  }
}
// REDIRECTED: Now routes to new desktop Hall of Fame template
export async function showLeaderboardOverlay() {
  console.log("🏆 Redirecting to new desktop Hall of Fame template...");
  
  // Get the current active scene to transition from
  const currentScene = window.game?.scene?.getScene(window.game?.scene?.manager?.keys?.find(key => 
    window.game?.scene?.isActive(key)
  ));
  
  if (currentScene && currentScene.startScene) {
    currentScene.startScene('HallOfFameDesktopTemplate');
  } else {
    // Fallback: Start scene directly
    console.warn('No active scene found, starting Hall of Fame directly');
    if (window.game?.scene) {
      window.game.scene.start('HallOfFameDesktopTemplate');
    }
  }
}

// DEBUG: Hall of Fame system helper
window.debugHallOfFame = function() {
  console.log('=== HALL OF FAME DEBUG INFO ===');
  console.log('Current flags:');
  console.log('  pptHeroUseDOMHallOfFame (deprecated):', localStorage.getItem('pptHeroUseDOMHallOfFame'));
  console.log('  pptHeroUseLegacyHallOfFame (current):', localStorage.getItem('pptHeroUseLegacyHallOfFame'));
  console.log('');
  console.log('System behavior:');
  console.log('  Will use DOM Hall of Fame:', localStorage.getItem('pptHeroUseLegacyHallOfFame') !== 'true');
  console.log('  Will use Legacy Hall of Fame:', localStorage.getItem('pptHeroUseLegacyHallOfFame') === 'true');
  console.log('');
  console.log('To force new DOM Hall of Fame: localStorage.removeItem("pptHeroUseLegacyHallOfFame")');
  console.log('To force legacy Hall of Fame: localStorage.setItem("pptHeroUseLegacyHallOfFame", "true")');
};

// AUTO-CLEANUP: Remove old deprecated flag and ensure new system is default
if (localStorage.getItem('pptHeroUseDOMHallOfFame') === 'true') {
  console.log('🧼 Cleaning up deprecated pptHeroUseDOMHallOfFame flag');
  localStorage.removeItem('pptHeroUseDOMHallOfFame');
}

// Ensure legacy flag is not accidentally set
if (!localStorage.getItem('pptHeroUseLegacyHallOfFame')) {
  // This ensures the beautiful new DOM Hall of Fame is the default
  console.log('🎆 Hall of Fame DOM is now the default system');
}

// TEST FUNCTION: Verify Hall of Fame routing
window.testHallOfFame = async function() {
  console.log('🧪 Testing Hall of Fame routing...');
  try {
    await showLeaderboardOverlay();
    console.log('✅ Hall of Fame test completed successfully');
  } catch (error) {
    console.error('❌ Hall of Fame test failed:', error);
  }
};

// Legacy leaderboard system (renamed)
async function showLegacyLeaderboardOverlay() {
  // Check if auth overlay is active or just closed - prevent conflicts
  const authOverlayElement = document.getElementById('auth-overlay');
  const isAuthOverlayVisible = authOverlayElement && (authOverlayElement.classList.contains('show') || authOverlayElement.style.display === 'flex');
  const authCloseTime = UIController?.recentOverlayActions?.lastClosedOverlay === 'auth-overlay' ? (UIController?.recentOverlayActions?.closeTime || 0) : 0;
  const timeSinceAuthClose = Date.now() - authCloseTime;

  if (isAuthOverlayVisible || (UIController?.recentOverlayActions?.lastClosedOverlay === 'auth-overlay' && timeSinceAuthClose < 500)) {
    console.log("Auth overlay active or just closed - preventing leaderboard from showing (authUI check)");
    return; // Exit early
  }
  
  console.log("showLegacyLeaderboardOverlay called - Populating content...");
  if (!cacheLeaderboardElements()) {
    alert("UI Error: Leaderboard cannot be displayed. Required elements missing.");
    console.error("Aborting showLegacyLeaderboardOverlay - critical elements missing.");
    return;
  }

  // Pause game scene if needed
  const gameScene = window.gameInstance?.getActiveScene?.();
  if (gameScene && ['GameScene', 'PPTHeroScene', 'MultiplayerGameScene'].includes(gameScene.scene.key)) {
    try {
      gameScene.physics?.pause();
      gameScene.scene?.pause();
    } catch (e) {
      console.warn("Error pausing scene for leaderboard", e);
    }
  }

  leaderboardList.innerHTML = '<div class="loading-indicator">Loading scores...</div>';
  leaderboardTabs.innerHTML = '';

  try {
    createImprovedLeaderboardTabs();
    await displayFilteredLeaderboard();
    if (UIController && typeof UIController.showOverlay === 'function') {
      UIController.showOverlay('leaderboard-overlay');
    } else {
      leaderboardOverlay.style.display = 'flex';
      void leaderboardOverlay.offsetHeight;
      leaderboardOverlay.classList.add("show");
    }
  } catch (error) {
    console.error("Error populating leaderboard:", error);
    leaderboardList.innerHTML = '<div class="message-area error-message" style="display: block;">Failed to load leaderboard.</div>';
    if (UIController && typeof UIController.showOverlay === 'function') {
      UIController.showOverlay('leaderboard-overlay');
    }
  }
}

export function hideLeaderboardOverlay() {
  if (UIController && typeof UIController.hideOverlay === 'function') {
    UIController.hideOverlay('leaderboard-overlay');
  } else if (leaderboardOverlay) {
    leaderboardOverlay.classList.remove("show");
    leaderboardOverlay.style.display = 'none';
  }

  // Resume game scenes if they were paused
  try {
    const gameSceneKeys = ["GameScene", "PPTHeroScene", "MultiplayerGameScene"];
    gameSceneKeys.forEach(key => {
      const scene = window.gameInstance?.scene?.getScene(key);
      if (scene && scene.scene.isPaused()) {
        console.log(`Resuming scene: ${key}`);
        scene.scene.resume();
        if (scene.physics?.isPaused) {
          scene.physics.resume();
        }
      }
    });
  } catch (e) {
    console.warn("Error resuming game scenes after leaderboard:", e);
  }
}

function createImprovedLeaderboardTabs() {
  if (!leaderboardTabs) return;
  leaderboardTabs.innerHTML = '';

  // Level 1 game filters
  const gameFilters = [
    { text: 'PPT Hero', game: 'PPTHero' },
    { text: 'Slide Wars', game: 'Shortcut' },
    { text: 'All Games', game: 'All' }
  ];
  
  // Level 2 mode filters
  const modeFilters = [
    { text: 'Solo', mode: 'Solo' },
    { text: 'Team', mode: 'Team' },
    { text: 'All', mode: 'All' }
  ];

  // Create Level 1 button container
  const gameFilterContainer = document.createElement('div');
  gameFilterContainer.className = 'filter-group level-one';
  gameFilterContainer.style.display = 'flex';
  gameFilterContainer.style.justifyContent = 'center';
  gameFilterContainer.style.gap = '10px';
  gameFilterContainer.style.marginBottom = '10px';
  gameFilterContainer.style.width = '100%';

  // Create Level 2 button container
  const modeFilterContainer = document.createElement('div');
  modeFilterContainer.className = 'filter-group level-two';
  modeFilterContainer.style.display = 'flex';
  modeFilterContainer.style.justifyContent = 'center';
  modeFilterContainer.style.gap = '10px';
  modeFilterContainer.style.width = '100%';

  // Create Level 1 buttons
  gameFilters.forEach(filter => {
    const button = createFilterButton(filter, 'game');
    gameFilterContainer.appendChild(button);
  });

  // Create Level 2 buttons
  modeFilters.forEach(filter => {
    // For Team mode, we need to check if it should be shown based on the current game selection
    if (filter.mode === 'Team' && currentLeaderboardFilter.game !== 'All' && !showTeamOption(currentLeaderboardFilter.game)) {
      return;
    }
    const button = createFilterButton(filter, 'mode');
    modeFilterContainer.appendChild(button);
  });

  // Add button containers to tabs
  leaderboardTabs.appendChild(gameFilterContainer);
  leaderboardTabs.appendChild(modeFilterContainer);
}
function createFilterButton(filter, type) {
  const button = document.createElement('button');
  button.textContent = filter.text;
  button.className = 'filter-button';
  
  // Apply default button styles
  Object.assign(button.style, {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontFamily: 'monospace',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: '#444444',
    color: '#FFFFFF',
    minWidth: type === 'game' ? '120px' : '80px'
  });
  
  // Check if button should be active
  const isActive = 
    (type === 'game' && currentLeaderboardFilter.game === filter.game) ||
    (type === 'mode' && currentLeaderboardFilter.mode === filter.mode);
  
  // Apply active styles
  if (isActive) {
    Object.assign(button.style, {
      backgroundColor: type === 'game' ? '#00AAFF' : '#00CC88',
      transform: 'scale(1.05)',
      fontWeight: 'bold'
    });
    button.classList.add('active');
  }

  // Add hover effects
  button.onmouseover = () => {
    if (!button.classList.contains('active')) {
      button.style.backgroundColor = type === 'game' ? '#0088CC' : '#009966';
      button.style.transform = 'scale(1.03)';
    }
  };
  
  button.onmouseout = () => {
    if (!button.classList.contains('active')) {
      button.style.backgroundColor = '#444444';
      button.style.transform = 'scale(1)';
    }
  };

  // Add click handler
  button.onclick = () => {
    // If trying to select Team mode but not compatible with game, don't do anything
    if (type === 'mode' && filter.mode === 'Team' && currentLeaderboardFilter.game !== 'All' && !showTeamOption(currentLeaderboardFilter.game)) {
      return;
    }
    
    if (type === 'game') {
      currentLeaderboardFilter.game = filter.game;
      
      // Reset to 'All' mode if selecting a game type that doesn't support Team
      if (filter.game !== 'All' && currentLeaderboardFilter.mode === 'Team' && !showTeamOption(filter.game)) {
        currentLeaderboardFilter.mode = 'All';
      }
    } else { // mode
      currentLeaderboardFilter.mode = filter.mode;
    }
    
    // Recreate tabs and refresh leaderboard
    createImprovedLeaderboardTabs();
    displayFilteredLeaderboard();
  };
  
  return button;
}
/**
 * Displays filtered leaderboard with improved layout and data handling
 * @returns {Promise<void>}
 */
async function displayFilteredLeaderboard() {
  cacheLeaderboardElements();
  if (!leaderboardList || !leaderboardContent) {
    console.error("Cannot display leaderboard: list or content div missing.");
    leaderboardList.innerHTML = '<div class="error-message">UI Error: Leaderboard display area not found.</div>';
    return;
  }
  
  leaderboardList.innerHTML = '<div class="loading-indicator">Fetching scores...</div>';

  try {
    let scores = [];
    let title = "Leaderboard";
    const isTeamView = currentLeaderboardFilter.mode === 'Team';

    // Fetch appropriate scores based on current filter
    if (isTeamView) {
      title = "Top Team Scores";
      
      // For team scores, always query the teamScores collection in Firestore
      let teamQuery;
      
      if (currentLeaderboardFilter.game === 'PPTHero') {
        // PPT Hero team scores
        teamQuery = db.collection('teamScores')
          .where('gameType', '==', 'PPTHero')
          .orderBy('totalScore', 'desc')
          .limit(10);
          
        title = "PPT Hero - Team Scores";
      } else if (currentLeaderboardFilter.game === 'Shortcut') {
        // Slide Wars team scores
        teamQuery = db.collection('teamScores')
          .where('gameType', 'in', ['Shortcut', 'MultiplayerShortcut', 'slide wars', 'Slide Wars'])
          .orderBy('totalScore', 'desc')
          .limit(10);
          
        title = "Slide Wars - Team Scores";
      } else {
        // All games - just order by totalScore
        teamQuery = db.collection('teamScores')
          .orderBy('totalScore', 'desc')
          .limit(10);
      }
      
      // Execute the query
      const teamSnap = await teamQuery.get();
      console.log(`Team query returned ${teamSnap.size} results for game type: ${currentLeaderboardFilter.game}`);
      
      // Map the team scores data
      scores = teamSnap.docs.map((doc, index) => {
        const data = doc.data();
        return {
          rank: index + 1,
          teamName: data.teamName || '-',
          players: data.playerNames || '-',
          score: data.totalScore || 0,
          game: data.gameType || 'Unknown',
          date: data.timestamp?.toDate() || data.createdAt?.toDate() || new Date()
        };
      });
    } else {
      // Individual scores query
      let query = db.collection('scores');
      
      if (currentLeaderboardFilter.game === 'PPTHero') {
        query = query.where('game', '==', 'PPTHero');
        title = 'PPT Hero - ';
      } else if (currentLeaderboardFilter.game === 'Shortcut') {
        query = query.where('game', 'in', ['Shortcut', 'MultiplayerShortcut', 'slide wars', 'Slide Wars']);
        title = 'Slide Wars - ';
      } else {
        title = 'All Games - ';
      }
      
      query = query.orderBy('score', 'desc').limit(50);
      const individualSnap = await query.get();
      let fetchedScores = [];
      individualSnap.forEach(doc => fetchedScores.push({ id: doc.id, ...doc.data() }));

      if (currentLeaderboardFilter.mode === 'Solo') {
        title += 'Solo Top Scores';
        scores = fetchedScores.filter(s => !s.teamName);
      } else {
        title += 'All Time Top Scores';
        scores = fetchedScores;
      }

      // Format scores for table display
      scores = scores.slice(0, 10).map((data, index) => ({
        rank: index + 1,
        player: data.player || 'Player',
        team: data.teamName || '-',
        score: data.score || 0,
        game: data.game || '?',
        date: data.createdAt?.toDate(),
        verified: data.verified === true
      }));
    }

    // Clear and populate leaderboard
    leaderboardList.innerHTML = '';

    // Add title
    const titleElement = document.createElement('h3');
    titleElement.textContent = title;
    titleElement.className = 'leaderboard-view-title';
    titleElement.style.marginBottom = '15px';
    titleElement.style.textAlign = 'center';
    leaderboardList.appendChild(titleElement);

    // Check if scores are available
    if (scores.length === 0) {
      const noScoresElement = document.createElement('p');
      noScoresElement.className = 'no-scores';
      noScoresElement.textContent = 'No scores found for this filter.';
      noScoresElement.style.textAlign = 'center';
      noScoresElement.style.padding = '20px';
      leaderboardList.appendChild(noScoresElement);
      return;
    }

    // Create appropriate table
    let tableElement;
    if (isTeamView) {
      tableElement = createImprovedTeamLeaderboardTable(scores);
    } else {
      tableElement = createImprovedIndividualLeaderboardTable(scores);
    }

    if (tableElement instanceof Node) {
      leaderboardList.appendChild(tableElement);
    } else {
      console.error("Failed to create leaderboard table element.");
      leaderboardList.innerHTML += '<p class="error-message">Error displaying scores table.</p>';
    }

    // Add legend
    const noteDiv = document.createElement("div");
    noteDiv.className = 'leaderboard-note';
    noteDiv.style.marginTop = "15px";
    noteDiv.style.fontSize = '12px';
    noteDiv.style.color = "#CCCCCC";
    noteDiv.style.textAlign = 'center';
    noteDiv.innerHTML = `<span style="color:#10b981">★</span> Verified player.`; // Professional success green
    leaderboardList.appendChild(noteDiv);
  } catch (error) {
    console.error("Error fetching/displaying filtered leaderboard:", error);
    leaderboardList.innerHTML = '<div style="text-align: center; color: red; padding: 20px;">Failed to load scores. Check Permissions & Console.</div>';
  }
}
function showTeamOption(gameType) {
  // In the real implementation, this would check the actual capabilities of each game
  // For this example, we'll implement a simplified version that shows team mode for all games
  if (gameType === 'PPTHero' || gameType === 'Shortcut' || gameType === 'All') {
    return true;
  }
  
  // Default to false for any other game type
  return false;
}

// In authUI.js

/**
 * Creates an improved team leaderboard table with better styling and formatting
 * @param {Array} scores - Team score data
 * @returns {HTMLTableElement} - Formatted table
 */
function createImprovedTeamLeaderboardTable(scores) {
  const table = document.createElement('table');
  table.className = 'leaderboard-table';

  // Add table styles (keep existing styles)
  Object.assign(table.style, {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    // Remove the global textAlign: 'center' here if it exists
  });

  // Create header
  const thead = table.createTHead();
  const headerRow = thead.insertRow();
  headerRow.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';

  // Define columns, widths, and ALIGNMENT
  const headers = [
    { text: 'Rank', width: '10%', align: 'center' },
    { text: 'Team Name', width: '25%', align: 'left' }, // Align left
    { text: 'Players', width: '30%', align: 'left' }, // Align left
    { text: 'Score', width: '15%', align: 'center' },
    { text: 'Game', width: '10%', align: 'center' },
    { text: 'Date', width: '10%', align: 'center' }
  ];

  // Add headers with alignment
  headers.forEach(header => {
    const th = document.createElement('th');
    th.textContent = header.text;
    th.style.padding = '10px';
    th.style.fontWeight = 'bold';
    th.style.width = header.width;
    th.style.textAlign = header.align; // Apply alignment
    headerRow.appendChild(th);
  });

  // Create table body with rows
  const tbody = table.createTBody();
  scores.forEach((score, index) => {
    const row = tbody.insertRow();

    // Add zebra striping (keep existing)
    row.style.backgroundColor = index % 2 === 0 ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.2)';

    // Add row hover effect (keep existing)
    row.onmouseover = () => { row.style.backgroundColor = 'rgba(0, 100, 255, 0.2)'; };
    row.onmouseout = () => { row.style.backgroundColor = index % 2 === 0 ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.2)'; };

    // Format game display (keep existing)
    let gameDisplay = score.game || 'Unknown';
    if (gameDisplay === 'PPTHero') gameDisplay = 'PPT';
    if (['Shortcut', 'MultiplayerShortcut', 'slide wars', 'Slide Wars'].includes(gameDisplay)) {
      gameDisplay = 'Slide';
    }

    // Format date (keep existing)
    let dateDisplay = '-';
    if (score.date instanceof Date) {
      dateDisplay = score.date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    }

    // --- NEW: Format players list ---
    let playersHtml = '-';
    if (Array.isArray(score.players)) {
        // Join array elements with <br> for new lines
        playersHtml = score.players.join('<br>');
    } else if (typeof score.players === 'string' && score.players.length > 0) {
        // If it's a string, replace commas with <br>
        playersHtml = score.players.replace(/,\s*/g, '<br>');
    }
    // --- END NEW ---

    // Add cells with data - map headers to know alignment and type
    const cellData = [
        { value: score.rank, align: headers[0].align },
        { value: score.teamName || '-', align: headers[1].align },
        { value: playersHtml, align: headers[2].align, isHTML: true }, // Use formatted playersHtml
        { value: score.score.toLocaleString(), align: headers[3].align },
        { value: gameDisplay, align: headers[4].align },
        { value: dateDisplay, align: headers[5].align }
    ];

    // Create cells with consistent styling and alignment
    cellData.forEach(cell => {
        const td = document.createElement('td');
        if (cell.isHTML) {
            td.innerHTML = cell.value; // Use innerHTML for the players list
        } else {
            td.textContent = cell.value;
        }
        td.style.padding = '8px';
        td.style.textAlign = cell.align; // Apply alignment
        row.appendChild(td);
    });
  });

  return table;
}

function createImprovedIndividualLeaderboardTable(scores) {
  const table = document.createElement('table');
  table.className = 'leaderboard-table';
  
  // Add table styles
  Object.assign(table.style, {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    textAlign: 'center'
  });
  
  // Create header
  const thead = table.createTHead();
  const headerRow = thead.insertRow();
  headerRow.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
  
  // Define columns and widths
  const headers = [
    { text: 'Rank', width: '10%' },
    { text: 'Player', width: '25%' },
    { text: 'Team', width: '15%' },
    { text: 'Score', width: '15%' },
    { text: 'Game', width: '15%' },
    { text: 'Date', width: '20%' }
  ];
  
  // Add headers
  headers.forEach(header => {
    const th = document.createElement('th');
    th.textContent = header.text;
    th.style.padding = '10px';
    th.style.fontWeight = 'bold';
    th.style.width = header.width;
    headerRow.appendChild(th);
  });
  
  // Create table body with rows
  const tbody = table.createTBody();
  scores.forEach((score, index) => {
    const row = tbody.insertRow();
    
    // Add zebra striping
    row.style.backgroundColor = index % 2 === 0 ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.2)';
    
    // Highlight current user's score if present
    if (score.highlight) {
      row.style.backgroundColor = 'rgba(0, 255, 0, 0.1)';
      row.style.fontWeight = 'bold';
    }
    
    // Add row hover effect
    row.onmouseover = () => {
      row.style.backgroundColor = score.highlight ? 'rgba(0, 255, 0, 0.2)' : 'rgba(0, 100, 255, 0.2)';
    };
    row.onmouseout = () => {
      if (score.highlight) {
        row.style.backgroundColor = 'rgba(0, 255, 0, 0.1)';
      } else {
        row.style.backgroundColor = index % 2 === 0 ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.2)';
      }
    };
    
    // Format game name display
    let gameDisplay = score.game || '-';
    if (gameDisplay === 'PPTHero') gameDisplay = 'PPT';
    if (gameDisplay === 'Shortcut' || gameDisplay === 'MultiplayerShortcut' || gameDisplay === 'slide wars' || gameDisplay === 'Slide Wars') {
      gameDisplay = 'Slide';
    }
    
    // Add verified star if applicable
    const verifiedStar = score.verified ? '<span class="verified-star" title="Verified">★</span>' : '';
    
    // Add cells with data
    const cells = [
      { value: score.rank, className: 'rank' },
      { value: `${score.player}${verifiedStar}`, className: 'player', isHTML: true },
      { value: score.team || '-', className: 'team' },
      { value: score.score.toLocaleString(), className: 'score' },
      { value: gameDisplay, className: 'game' },
      { value: score.date ? score.date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '-', className: 'date' }
    ];
    
    // Create cells with consistent styling
    cells.forEach(cell => {
      const td = document.createElement('td');
      if (cell.isHTML) {
        td.innerHTML = cell.value;
      } else {
        td.textContent = cell.value;
      }
      td.className = cell.className;
      td.style.padding = '8px';
      td.style.textAlign = 'center';
      row.appendChild(td);
    });
  });
  
  return table;
}

function createIndividualLeaderboardTable(scores) {
  const table = document.createElement('table');
  table.className = 'leaderboard-table';
  const thead = table.createTHead();
  const headerRow = thead.insertRow();
  const headers = ['Rank', 'Player', 'Team', 'Game', 'Score', 'Date'];
  headers.forEach(text => {
    const th = document.createElement('th');
    th.textContent = text;
    headerRow.appendChild(th);
  });
  const tbody = table.createTBody();
  scores.forEach(score => {
    const row = tbody.insertRow();
    const verifiedStar = score.verified ? '<span class="verified-star" title="Verified">★</span>' : '';
    row.innerHTML = `
      <td class="rank">${score.rank}</td>
      <td class="player">${score.player}${verifiedStar}</td>
      <td class="team-name">${score.team || '-'}</td>
      <td class="game">${score.game || '?'}</td>
      <td class="score">${score.score.toLocaleString()}</td>
      <td class="date">${score.date ? score.date.toLocaleDateString('en-GB') : '-'}</td>
    `;
  });
  return table;
}

// --- Event Listeners for Auth ---
async function handleSignIn() {
  console.log("Sign In button clicked.");
  
  // ENTERPRISE REFACTOR: Try EnterpriseAuthService first, fallback to original logic
  if (window.enterpriseAuthService && window.enterpriseAuthService.isReady()) {
    console.log("🏢 Using EnterpriseAuthService for handleSignIn");
    
    if (!authEmailInput || !authPasswordInput) { cacheAuthElements(); }
    if (!authEmailInput || !authPasswordInput) { 
      console.error("Sign In failed: Input elements not found."); 
      setAuthMessage("UI Error: Input fields not found.");
      return; 
    }
    
    const email = authEmailInput.value.trim();
    const pass = authPasswordInput.value;
    
    // Input validation only (let authService handle everything else)
    if (!email || !pass) { 
      setAuthMessage("Please fill all fields."); 
      return; 
    }
    
    setAuthMessage(""); // Clear messages
    showLoadingIndicator('signin-submit-btn-auth');
    
    try {
      // CRITICAL: Let existing authService handle EVERYTHING - no double processing
      const result = await window.enterpriseAuthService.handleSignIn(email, pass);
      
      // authService already handled overlay hiding, auth events, score claiming
      // We only need to handle loading indicator here
      hideLoadingIndicator('signin-submit-btn-auth');
      
      // If authService returned false (handled internally), don't do anything more
      if (result === false) {
        console.log("🔄 authService handled sign in internally (no further action needed)");
        return;
      }
      
    } catch (error) {
      hideLoadingIndicator('signin-submit-btn-auth');
      setAuthMessage(`Sign-in error: ${error.message}`);
    }
    
    return;
  }
  
  // FALLBACK: Original logic
  console.log("📝 Using original handleSignIn logic");
  if (!authEmailInput || !authPasswordInput) { cacheAuthElements(); } // Re-cache if needed
  if (!authEmailInput || !authPasswordInput) { console.error("Sign In failed: Input elements not found."); return; }
  const email = authEmailInput.value.trim();
  const pass = authPasswordInput.value;
  if (!email || !pass) { setAuthMessage("Please fill all fields."); return; }
  setAuthMessage("");
  showLoadingIndicator('signin-submit-btn-auth');
  signInWithEmail(email, pass)
      .then(userCredential => {
          hideLoadingIndicator('signin-submit-btn-auth');
          if (userCredential && userCredential.user && userCredential.user.emailVerified) {
              // SUCCESS: Auth logic in index.js handles scene transition via SceneManager
              // We only need to hide the overlay here.
              hideAuthOverlay(); // Use the function which delegates to UIController
          } else if (userCredential === false) {
              console.log("signInWithEmail resolved false (error handled internally).");
              // Message is already shown by signInWithEmail
          } else {
              // Should not happen if signInWithEmail is implemented correctly
              setAuthMessage("Authentication failed. Please try again or sign up.");
          }
      })
      .catch(error => { // Catch should ideally not be needed if signInWithEmail handles errors
          hideLoadingIndicator('signin-submit-btn-auth');
          setAuthMessage(`Sign-in error: ${error.message}`);
      });
}

async function handleSignUp() {
  console.log("Sign Up button clicked.");
  
  // ENTERPRISE REFACTOR: Try EnterpriseAuthService first, fallback to original logic
  if (window.enterpriseAuthService && window.enterpriseAuthService.isReady()) {
    console.log("🏢 Using EnterpriseAuthService for handleSignUp");
    
    if (!authEmailInput || !authPasswordInput || !signupUsernameInput || !signupPassword2Input || !privacyConsentAuth) { cacheAuthElements(); }
    if (!authEmailInput || !authPasswordInput || !signupUsernameInput || !signupPassword2Input || !privacyConsentAuth) { 
      console.error("Sign Up failed: Input elements not found."); 
      setAuthMessage("UI Error: Input fields not found.");
      return; 
    }
    
    // Input validation (let authService handle the rest)
    const email = authEmailInput.value.trim();
    const pass = authPasswordInput.value;
    const confirmPass = signupPassword2Input.value;
    const username = signupUsernameInput.value.trim();
    
    if (!email || !pass || !username) { 
      setAuthMessage("Please fill all fields."); 
      return; 
    }
    if (pass !== confirmPass) { 
      setAuthMessage("Passwords do not match."); 
      return; 
    }
    if (!privacyConsentAuth.checked) { 
      setAuthMessage("You must agree to the Privacy Policy."); 
      return; 
    }
    
    setAuthMessage(""); // Clear messages
    showLoadingIndicator('signup-submit-btn-auth');
    
    try {
      // CRITICAL: Let existing authService handle EVERYTHING - no double processing
      const result = await window.enterpriseAuthService.handleSignUp(
        email, 
        pass, 
        username, 
        marketingConsentAuth?.checked || false, 
        dataSharingConsentAuth?.checked || false
      );
      
      hideLoadingIndicator('signup-submit-btn-auth');
      
      if (result.success) {
        // SUCCESS: Show success message, user needs to verify email (preserving original behavior)
        setAuthMessage(result.message, false); // Show success message (false = not an error)
        setTimeout(() => {
          if (document.getElementById('auth-overlay')?.classList.contains('show')) { // Check if overlay still open
            showAuthOverlay('signin'); // Switch to sign-in view
            setAuthMessage("Please check your email to verify your account before signing in.", false);
          }
        }, 3000);
      } else {
        setAuthMessage(result.message); // Show error message
      }
      
    } catch (error) {
      hideLoadingIndicator('signup-submit-btn-auth');
      setAuthMessage(`Sign-up error: ${error.message}`);
    }
    
    return;
  }
  
  // FALLBACK: Original logic
  console.log("📝 Using original handleSignUp logic");
  if (!authEmailInput || !authPasswordInput || !signupUsernameInput || !signupPassword2Input || !privacyConsentAuth) { cacheAuthElements(); }
  if (!authEmailInput || !authPasswordInput || !signupUsernameInput || !signupPassword2Input || !privacyConsentAuth) { console.error("Sign Up failed: Input elements not found."); return; }
  const email = authEmailInput.value.trim();
  const pass = authPasswordInput.value;
  const confirmPass = signupPassword2Input.value;
  const username = signupUsernameInput.value.trim();
  if (!email || !pass || !username) { setAuthMessage("Please fill all fields."); return; }
  if (pass !== confirmPass) { setAuthMessage("Passwords do not match."); return; }
  if (!privacyConsentAuth.checked) { setAuthMessage("You must agree to the Privacy Policy."); return; } // Ensure privacy consent
  setAuthMessage("");
  showLoadingIndicator('signup-submit-btn-auth');
  signUpWithEmail(email, pass, username, marketingConsentAuth.checked, dataSharingConsentAuth.checked)
      .then(result => {
          hideLoadingIndicator('signup-submit-btn-auth');
          if (result.success) {
              // SUCCESS: Show success message, user needs to verify email.
              setAuthMessage(result.message, false); // Show success message (false = not an error)
              // Optionally switch back to sign-in view after a delay
              setTimeout(() => {
                  if (document.getElementById('auth-overlay')?.classList.contains('show')) { // Check if overlay still open
                       showAuthOverlay('signin'); // Switch to sign-in view
                       setAuthMessage("Please check your email to verify your account before signing in.", false);
                  }
              }, 3000);
          } else {
              setAuthMessage(result.message); // Show error message from signUpWithEmail
          }
      })
      .catch(error => { // Catch should ideally not be needed
          hideLoadingIndicator('signup-submit-btn-auth');
          setAuthMessage(`Sign-up error: ${error.message}`);
      });
}


// --- REPLACE setupAuthEventListeners ---
export function setupAuthEventListeners() {
  console.log("Setting up Auth Event Listeners...");
  cacheAuthElements(); // Ensure elements are cached

  if (!authOverlay) {
      console.error("Auth overlay not found during listener setup.");
      return;
  }

  // Helper to safely add/replace listeners
  const safeAddListener = (elementId, eventType, handler) => {
      const element = document.getElementById(elementId);
      if (element) {
          // Clone and replace to remove old listeners reliably
          const newElement = element.cloneNode(true);
          element.parentNode.replaceChild(newElement, element);
          newElement.addEventListener(eventType, handler);
          console.debug(`Listener added for ${elementId} (${eventType})`);
          // Update cached reference if needed (e.g., for buttons)
          if (elementId === 'signin-submit-btn-auth') signinSubmitBtn = newElement;
          if (elementId === 'signup-submit-btn-auth') signupSubmitBtn = newElement;
          // ... update other cached elements if necessary ...
      } else {
          console.warn(`Element #${elementId} not found for listener setup.`);
      }
  };

  // Setup listeners using the helper
  safeAddListener("auth-close-btn", "click", hideAuthOverlay);
  safeAddListener("switch-to-signup-link", "click", (e) => { e.preventDefault(); showAuthOverlay('signup'); });
  safeAddListener("switch-to-signin-link", "click", (e) => { e.preventDefault(); showAuthOverlay('signin'); });
  safeAddListener("signin-submit-btn-auth", "click", handleSignIn);
  safeAddListener("signup-submit-btn-auth", "click", handleSignUp);

  // Ensure message div exists (keep this check)
  if (!authMessageDiv && authOverlayContent) { /* ... create message div ... */ }

  console.log("Auth Event Listeners setup complete.");
}



// Integration with Professional Authentication Dialogs
let professionalAuthDialogs;

// Initialize professional auth dialogs when available - RESTORED
document.addEventListener('DOMContentLoaded', () => {
    // Wait for auth dialogs to initialize
    setTimeout(() => {
        if (window.authDialogs) {
            professionalAuthDialogs = window.authDialogs;
            console.log('✅ Professional Authentication Dialogs integrated with authUI (RESTORED)');
        }
    }, 100);
});

// Enhanced auth overlay functions that use professional dialogs - RESTORED
export function showSignInDialog() {
    console.log('🎨 Showing professional sign in dialog (RESTORED)');
    if (professionalAuthDialogs) {
        professionalAuthDialogs.showSignIn();
    } else {
        console.warn('Professional auth dialogs not available, falling back to basic auth');
        showAuthOverlay('signin');
    }
}

export function showSignUpDialog() {
    console.log('🎨 Showing professional sign up dialog (RESTORED)');
    if (professionalAuthDialogs) {
        professionalAuthDialogs.showSignUp();
    } else {
        console.warn('Professional auth dialogs not available, falling back to basic auth');
        showAuthOverlay('signup');
    }
}

// Enhanced showAuthOverlay function - integrates professional dialogs
// This removes the duplicate export declaration that was causing the error

// If you're using a default export at the end of the file, make sure to include these functions:
const authUI = {
  showAuthOverlay,
  hideAuthOverlay,
  showLeaderboardOverlay,
  hideLeaderboardOverlay,
  setupAuthEventListeners,
  setAuthMessage
  // ...any other exported functions
};

export default authUI;
